import { ListingFields } from "../../helpers/Listing.js";
import { ProductFields } from "../../helpers/Product.js";
import { boxOptions, conditionOptions } from "../../product/constants.js";
import DataHelper from "../../helpers/Data.js";
import { getBaseCurrency, symbolFor } from "../../core/currency.js";

/**
 * The listing half of voice dictation: the field schema the shared overlay
 * renders, and the seed listing the form is opened with.
 *
 * Nothing here is shared with Request creation — that arrives later as its own
 * schema file next to this one, and the overlay is untouched.
 *
 * V1 is watches only. The template sentence, the vocabulary and the checklist
 * all live in this one definition, so a second category is a new file plus its
 * own candidate card, not a rewrite.
 */

/** Money the form holds in MAJOR units — see processSubmitValues in helpers/Listing.js. */
export const MONEY_FIELDS = ["price", "buying_price", "retail_price"];

// Every mandatory field, keyed exactly as the form's own required lists name it.
//
// `derived: true` marks the ones a person never says: ebay_title is composed
// from brand + model + reference, and category_id / images are carried from the
// product record. They are mandatory on the form and filled silently — showing
// them in the panel would read as things the user failed to mention.
//
// `order` is the order they are said in, which is not the order the two
// required arrays happen to concatenate in.
const REQUIRED_META = {
  "product.brand_id": { label: "Brand", spoken: "brand", order: 1, mandatory: true },
  "product.model_id": { label: "Model", spoken: "model", order: 2, mandatory: true },
  "product.reference_number": { label: "Reference number", spoken: "reference_number", order: 3, mandatory: true },
  condition: { label: "Condition", spoken: "condition", order: 4, mandatory: true },
  box: { label: "Delivery set (box & papers)", spoken: "box", order: 5, mandatory: true },
  availability: { label: "Availability", spoken: "availability", order: 6, mandatory: true },
  ebay_title: { derived: true },
  "product.category_id": { derived: true },
  "product.images": { derived: true },
};

const titleise = (key) =>
  key.replace(/^product\./, "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * The checklist IS the form's required list, minus the fields nobody speaks.
 * Deriving it from the two arrays rather than hand-listing six rows is what
 * makes drift from the form's validation impossible rather than unlikely.
 */
export const buildChecklist = (
  listingRequired = ListingFields.required,
  productRequired = ProductFields.required
) =>
  [...listingRequired, ...productRequired.map((f) => `product.${f}`)]
    // Unmapped = a field the form started requiring since this file was
    // written. Prompt for it: a clumsy label beats a silent gap.
    .map((key, i) => ({
      key,
      spoken: key,
      label: titleise(key),
      order: 100 + i,
      mandatory: false,
      ...(REQUIRED_META[key] || {}),
    }))
    .filter((item) => !item.derived)
    .sort((a, b) => a.order - b.order)
    // Price is worth prompting for and must never read as a requirement: it is
    // in neither of the form's required lists, so it is never marked mandatory.
    .concat([
      { key: "price", spoken: "price", label: "Price (optional)", order: 7, mandatory: false },
    ]);

// Opportunistic fields — captured only when volunteered, shown as "also heard".
const EXTRA_LABELS = {
  year: "Year",
  price: "Asking price",
  buying_price: "Buying price",
  retail_price: "Retail price",
  currency: "Currency",
  serial_number: "Serial number",
  supplier: "Supplier",
  limited_number: "Limited number",
  case_diameter: "Case diameter",
  dial_color: "Dial colour",
  case_material: "Case material",
};

// ── Value normalisation ─────────────────────────────────────────────────────
// A dropdown value that is not one of the option ids renders as "—" while still
// sitting in the payload, so anything unrecognised is dropped rather than half
// applied. Same rule the Sell Request prefill follows (listingFlows/intakeApi.js).

const CONDITION_IDS = conditionOptions.map((o) => o.id);
const BOX_IDS = boxOptions.map((o) => o.id);
const AVAILABILITY_IDS = DataHelper.availability.map((o) => o.id);

const slug = (v) =>
  typeof v === "string" ? v.trim().toLowerCase().replace(/[\s_]+/g, "-") : undefined;

const oneOf = (ids) => (v) => (ids.includes(slug(v)) ? slug(v) : undefined);

const NORMALISE = {
  condition: oneOf(CONDITION_IDS),
  box: oneOf(BOX_IDS),
  // Availability ids use underscores ("new_collection"), so slug() would mangle
  // them — compare on the raw lower-cased string instead.
  availability: (v) => {
    const raw = typeof v === "string" ? v.trim().toLowerCase() : undefined;
    return AVAILABILITY_IDS.includes(raw) ? raw : undefined;
  },
  year: (v) => (Number.isFinite(Number(v)) ? String(Number(v)) : undefined),
};

const LABEL_OF = {
  condition: Object.fromEntries(conditionOptions.map((o) => [o.id, o.name])),
  box: Object.fromEntries(boxOptions.map((o) => [o.id, o.name])),
  availability: Object.fromEntries(DataHelper.availability.map((o) => [o.id, o.name])),
};

/** The value as the FORM would hold it, or undefined if it can't be trusted. */
export const formValue = (key, fields) => {
  const raw = fields?.[key]?.value;
  if (raw === undefined || raw === null || raw === "") return undefined;
  const normalise = NORMALISE[key];
  return normalise ? normalise(raw) : raw;
};

// ── Money in words ──────────────────────────────────────────────────────────
// A misheard price is the most expensive error this feature can make, so every
// money figure is read back as words before the form opens. "£12,000" and
// "£1,200" are one glance apart; "twelve thousand" and "one thousand two
// hundred" are not.

const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
  "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen",
  "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

const underThousand = (n) => {
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? `-${ONES[n % 10]}` : "");
  const rest = n % 100;
  return `${ONES[Math.floor(n / 100)]} hundred${rest ? ` and ${underThousand(rest)}` : ""}`;
};

export const amountInWords = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < 0 || n >= 1e9) return null;
  if (n === 0) return "zero";

  const parts = [];
  const millions = Math.floor(n / 1e6);
  const thousands = Math.floor((n % 1e6) / 1000);
  const rest = n % 1000;
  if (millions) parts.push(`${underThousand(millions)} million`);
  if (thousands) parts.push(`${underThousand(thousands)} thousand`);
  if (rest) parts.push(underThousand(rest));
  return parts.join(" ");
};

const CURRENCY_WORDS = {
  gbp: "pounds", eur: "euros", usd: "dollars", hkd: "Hong Kong dollars",
  chf: "Swiss francs", aed: "dirhams", usdt: "tether",
};

const money = (value, currency) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${symbolFor(currency)}${n.toLocaleString("en-GB")}`;
};

// ── The schema the shared overlay renders ───────────────────────────────────


export const listingDictationSchema = {
  title: "Instant Listing Creation with voice",
  exampleLabel: "Example",
  hint:
    "Speak naturally. You can review every detail before the listing is created.",

  // A sentence a person would actually say to a colleague, shown FULLY
  // POPULATED with the swappable parts marked — a filled example is understood
  // at a glance where a row of empty slots has to be decoded first. It is a
  // prompt to the user and a hint to the model; it is never a parser, and
  // ignoring it entirely is handled exactly like free speech.
  template: [
    "I've got a ",
    { value: "2021", slot: "year" },
    " ",
    { value: "Rolex", slot: "brand" },
    " ",
    { value: "Submariner Date", slot: "model" },
    ", ref ",
    { value: "126610LN", slot: "reference_number" },
    ", ",
    { value: "unworn", slot: "condition" },
    ", ",
    { value: "full set", slot: "box" },
    ", ",
    { value: "ready to sell", slot: "availability" },
    ", asking ",
    { value: "twelve thousand pounds", slot: "price" },
    ".",
  ],

  // NO prompt list. The EXAMPLE sentence above it already names every field, in
  // the words someone would actually say — the list restated the same seven
  // things as bare nouns and pushed START RECORDING off a phone screen.
  // `buildChecklist` stays: it is the spec of which fields are spoken and which
  // are mandatory, and its tests are where that is pinned.

  // The overlay passes `hasCandidates` too; this schema's six rows are all
  // spoken, so none of them depends on a product having been picked.
  checklistStatus(item, { fields }) {
    const value = formValue(item.spoken, fields);
    if (value === undefined) return { done: false, text: "still needed" };
    return { done: true, text: this.formatValue(item.spoken, fields) };
  },

  extraLabel: (key) => EXTRA_LABELS[key] ?? null,

  // The sheet creates the listing from the six fields it shows plus year,
  // currency and price. Anything else it heard is displayed but NOT sent, and
  // saying so is cheaper and more honest than growing the panel.
  extrasNote(fields) {
    const notCarried = ["serial_number", "supplier", "buying_price", "retail_price", "limited_number"];
    const heard = notCarried.filter((key) => formValue(key, fields) !== undefined);
    if (!heard.length) return null;
    return `${heard.map((k) => EXTRA_LABELS[k]).join(", ")} ${
      heard.length === 1 ? "is" : "are"
    } not saved yet — add ${heard.length === 1 ? "it" : "them"} on the listing afterwards.`;
  },

  formatValue(key, fields) {
    const value = formValue(key, fields);
    if (value === undefined) return "";
    if (MONEY_FIELDS.includes(key)) {
      return money(value, slug(fields.currency?.value) || getBaseCurrency()) ?? String(value);
    }
    return LABEL_OF[key]?.[value] ?? String(value);
  },

  /** Every money field heard from speech, stated in words before the form opens. */
  heardMoney(fields) {
    const currency = slug(fields?.currency?.value) || getBaseCurrency();
    const unit = CURRENCY_WORDS[currency] ?? currency.toUpperCase();
    return MONEY_FIELDS.map((key) => {
      const value = formValue(key, fields);
      if (value === undefined) return null;
      const words = amountInWords(value);
      const figure = money(value, currency);
      return words
        ? `${EXTRA_LABELS[key]}: ${words} ${unit} (${figure})`
        : `${EXTRA_LABELS[key]}: ${figure}`;
    }).filter(Boolean);
  },

  selectionNote:
    "Nothing is saved until you press Create Listing on the form. If two watches were mentioned, only the first was taken.",

  emptyHint:
    "Nothing in the catalogue matched closely enough to offer. Here's what we heard — search for the watch manually and the rest of what was captured still carries over.",
};

export default listingDictationSchema;
